import {
  CommunicationOption,
  CustomerAddressInput,
  CustomerContactsInput,
  Maybe,
  PrivacyOptionInput,
  ProfileType,
  Salutation,
  TestDriveBookingAppointmentAttemptInput,
  TestDriveRequestAttemptInput,
  Title,
} from '../graphql/schema';
import FlowState from '../models/store/modules/otbc/flow-state.model';
import ConfigurationState from '../models/store/modules/configuration-state.model';
import UtilsService from './utils.service';
import { TestDriveMode } from '../models/utilities/testdrive-mode.enum';
import { Prospect } from '../models/entities/prospect.model';
import { TranslationService } from './translation.service';
import DateService from './date.service';

export class PrepareSelectionDataService {
  static buildRequest(
    state: FlowState,
    configuration: ConfigurationState
  ): TestDriveRequestAttemptInput {
    const { prospect, dealer, selectedDealer, isTestDriveFromHome } = state;
    const { context, locale, formConfig } = configuration;

    let pickMeUpAddress;
    if (isTestDriveFromHome)
      pickMeUpAddress = {
        pickMeUpAddress: PrepareSelectionDataService.buildAddress(prospect),
      };

    const aemFormsUtmParameters = prospect.customerOtherSet?.utmParameters;
    const urlUtmParameters = configuration.utmParameters;

    const payload: TestDriveRequestAttemptInput = {
      dealerId: dealer.baseInfo?.outletId || selectedDealer.id,
      appointmentPreferences:
        PrepareSelectionDataService.buildAppointmentPreferences(state),
      customer: {
        ...PrepareSelectionDataService.buildCustomer(prospect, formConfig),
        ...(pickMeUpAddress ?? {}),
      },
      countryCode: locale.countryCode,
      campaignId: prospect?.customerOtherSet?.campaignId || '',
      context,
      appointmentMessage:
        prospect?.customerOtherSet?.appointmentMessage || null,
      language: locale.language,
      trackingParameter: urlUtmParameters || aemFormsUtmParameters || null,
      sourceSystem: '', // it is not implemented in the state yet
    };

    return payload;
  }

  static buildBooking(
    state: FlowState,
    configuration: ConfigurationState
  ): TestDriveBookingAppointmentAttemptInput {
    const { prospect, selectedDealer } = state;
    const { context, locale, formConfig } = configuration;

    const aemFormsUtmParameters = prospect.customerOtherSet?.utmParameters;
    const urlUtmParameters = configuration.utmParameters;

    const payload: TestDriveBookingAppointmentAttemptInput = {
      dealerId: selectedDealer.id,
      appointmentPreferences:
        PrepareSelectionDataService.buildAppointmentPreferences(state),
      customer: PrepareSelectionDataService.buildCustomer(prospect, formConfig),
      countryCode: locale.countryCode,
      campaignId: prospect?.customerOtherSet?.campaignId || '',
      context,
      appointmentMessage:
        prospect?.customerOtherSet?.appointmentMessage || null,
      language: locale.language,
      trackingParameter: urlUtmParameters || aemFormsUtmParameters || null,
      sourceSystem: '', // it is not implemented in the state yet
    };

    return payload;
  }

  static buildAppointmentPreferences(state: FlowState): any {
    const { model, onlineCode, testDriveMode } = state;
    let vehicleAppointment = {};

    switch (testDriveMode) {
      case TestDriveMode.BOOKING:
        vehicleAppointment =
          PrepareSelectionDataService.buildBookingAppointmentPreferences(state);
        break;
      case TestDriveMode.REQUEST:
        vehicleAppointment =
          PrepareSelectionDataService.buildRequestAppointmentPreferences(state);
        break;
      default:
        break;
    }

    return UtilsService.cleanObject({
      modelSeriesId: model?.id || null,
      onlineCode: onlineCode || null,
      ...vehicleAppointment,
    });
  }

  static buildRequestAppointmentPreferences(state: FlowState): any {
    const {
      model,
      selectedPeriod,
      selectedFuelType,
      selectedTransmission,
      selectedMotorisation,
      selectedVehicleType, // it is not implemented in the state yet
      selectedDriveType,
      selectedWeight,
      selectedEngine, // it is not implemented in the state yet
      selectedWheelBase,
      isTestDriveFromHome,
    } = state;

    return UtilsService.cleanObject({
      driveType: selectedDriveType || null,
      motorisationId: selectedMotorisation?.id || null,
      fuelType: selectedFuelType || null,
      weightCategory: selectedWeight?.id || null,
      modelSeriesId: model?.id || null,
      engine: selectedEngine || null,
      schedule: {
        returnDateTime: selectedPeriod.end,
        returnDateStr: selectedPeriod.label.substr(8),
        pickUpDateStr: selectedPeriod.label.substr(0, 5),
        pickUpDateTime: selectedPeriod.start,
        dayPeriod: selectedPeriod.dayPeriod,
      },
      wheelBase: selectedWheelBase || null,
      pickMeUp: isTestDriveFromHome,
      vehicleTypes:
        selectedMotorisation?.vehicleTypes || selectedVehicleType || null,
      transmission: selectedTransmission || null,
    });
  }

  static buildBookingAppointmentPreferences(state: FlowState): any {
    const {
      model,
      selectedVehicle: {
        id,
        motorisationId,
        fuelType,
        weightCategory,
        engine,
        vehicleTypes,
        transmission,
        wheelBase,
        driveType,
      },
      selectedPeriod,
    } = state;

    UtilsService.removeKey('__typename', state.selectedVehicle);

    return UtilsService.cleanObject({
      vehicleId: id || null,
      motorisationId: motorisationId || null,
      fuelType: fuelType || null,
      weightCategory: weightCategory || null,
      driveType: driveType || null,
      wheelBase: wheelBase || null,
      modelSeriesId: model?.id || null,
      engine: engine || null,
      schedule: {
        returnDateTime: selectedPeriod.end,
        returnDateStr: selectedPeriod.label.substr(8),
        pickUpDateStr: selectedPeriod.label.substr(0, 5),
        pickUpDateTime: selectedPeriod.start,
      },
      vehicleTypes: vehicleTypes || null,
      transmission: transmission || null,
    });
  }

  static buildCustomer(prospect: Prospect, formConfig: any): any {
    const communicationOptions =
      PrepareSelectionDataService.buildCommunicationOptions(prospect);
    const privacyOptions = PrepareSelectionDataService.buildPrivacyOptions(
      prospect,
      formConfig
    );

    return {
      address: PrepareSelectionDataService.buildAddress(prospect),
      contacts: PrepareSelectionDataService.buildContacts(prospect),
      driverLicense: PrepareSelectionDataService.buildDriverLicense(prospect),
      declarationOfConsent: {
        communicationOptions,
        privacyOptions,
      },
      ...PrepareSelectionDataService.buildNames(prospect),
      salutation: PrepareSelectionDataService.getSalutation(prospect),
      title: PrepareSelectionDataService.getTitle(prospect),
      profileType: PrepareSelectionDataService.getProfileType(prospect),
      ciamId: prospect?.ciamId || null,
    };
  }

  static buildAddress(prospect: Prospect): CustomerAddressInput {
    return {
      city: prospect?.customerContactsSet?.city || null,
      houseNumber: prospect?.customerContactsSet?.houseNumber || null,
      postalCode: prospect?.customerContactsSet?.postCode || null,
      street: prospect?.customerContactsSet?.street || null,
    };
  }

  static buildContacts(prospect: Prospect): CustomerContactsInput {
    return {
      email: prospect?.customerContactsSet?.email || null,
      mobilePhone: prospect?.customerContactsSet?.mobilePhone || null,
      phone: prospect?.customerContactsSet?.phone || null,
    };
  }

  static buildDriverLicense(prospect: Prospect): Maybe<string> {
    return prospect?.customerOtherSet?.drivingLicense || null;
  }

  static buildNames(prospect: Prospect): any {
    return {
      firstName: prospect?.customerNameSet?.firstName,
      lastName: prospect?.customerNameSet?.lastName,
      companyName: prospect?.customerNameSet?.companyName,
      vatNumber: prospect?.customerNameSet?.vatNumber,
      middleName: prospect?.customerNameSet?.middleName,
      localizedFirstName: prospect?.customerNameSet?.localizedFirstName,
      localizedLastName: prospect?.customerNameSet?.localizedLastName,
    };
  }

  static getSalutation(prospect: Prospect): Salutation {
    return prospect?.customerNameSet?.salutation?.toUpperCase() as Salutation;
  }

  static getTitle(prospect: Prospect): Title {
    return prospect?.customerNameSet?.title?.toUpperCase() as Title;
  }

  static getProfileType(prospect: Prospect): ProfileType {
    return (
      (prospect?.customerOtherSet?.profileType as ProfileType) ??
      ProfileType.CONSUMER
    );
  }

  static buildCommunicationOptions(prospect: Prospect): CommunicationOption[] {
    const communicationOptions: CommunicationOption[] = [];

    if (prospect && prospect.contactOptionsSet) {
      Object.keys(prospect.contactOptionsSet)
        .filter((key: string) => !!prospect.contactOptionsSet[key])
        .forEach((item: string) =>
          communicationOptions.push(item.toUpperCase() as CommunicationOption)
        );
    }

    return communicationOptions;
  }

  static buildPrivacyOptions(
    prospect: Prospect,
    formConfig: any
  ): PrivacyOptionInput[] {
    const privacyOptions: Array<any> = [];
    const privacyOpts = formConfig?.aemForms?.enabled
      ? prospect.declarationOfConsentSet
      : formConfig?.fieldSets?.declarationOfConsentSet?.fields;

    if (privacyOpts) {
      Object.keys(privacyOpts).forEach((item: string) =>
        privacyOptions.push({
          consented: !!prospect.declarationOfConsentSet[item],
          id: item,
        })
      );
    }

    return privacyOptions;
  }

  static flattenSelection(
    selection: any,
    calendarLocale: string,
    highestVisitedStep: number
  ): any {
    const basicVehicleInfo = {
      selectedFuelType: TranslationService.translateOption(
        'fuel',
        selection.selectedFuelType
      ),
      selectedTransmission: TranslationService.translateOption(
        'transmission',
        selection.selectedTransmission
      ),
      selectedMotorisation:
        PrepareSelectionDataService.translateMotorisationOption(selection),
    };

    return Object.assign(
      {},
      PrepareSelectionDataService.addPrefix(selection.model, 'model-'),
      PrepareSelectionDataService.addPrefix(selection.dealer, 'dealer-'),
      PrepareSelectionDataService.addPrefix(selection.prospect, 'prospect-'),
      PrepareSelectionDataService.addPrefix(
        basicVehicleInfo,
        'basicVehicleInfo-'
      ),
      PrepareSelectionDataService.flattenSchedule(
        selection,
        calendarLocale,
        highestVisitedStep
      )
    );
  }

  static translateMotorisationOption({ selectedMotorisation }: any): string {
    return selectedMotorisation && selectedMotorisation.name;
  }

  static addPrefix(viewData: any, prefix: string): { [index: string]: string } {
    const receiverObjData: { [index: string]: string } = {};
    if (viewData) {
      Object.keys(viewData).forEach((key) => {
        if (viewData[key] && !Array.isArray(viewData[key])) {
          if (typeof viewData[key] === 'object') {
            Object.assign(
              receiverObjData,
              PrepareSelectionDataService.addPrefix(
                viewData[key],
                `${prefix}${key}-`
              )
            );
          } else {
            receiverObjData[prefix + key] = viewData[key];
          }
        }
      });
    }
    return receiverObjData;
  }

  static flattenSchedule(
    selection: any,
    calendarLocale: string,
    highestVisitedStep: number
  ): any {
    const period = DateService.buildPeriodLabel(
      selection.selectedPeriod,
      selection.bookingShown
    ) as string;
    return highestVisitedStep >= 2
      ? {
          'schedule-date-period': DateService.getSelectedPeriodSummary(
            selection.selectedDay,
            period,
            calendarLocale
          ),
        }
      : '';
  }
}
